import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { nanoid } from 'nanoid'
import { Checkbox, Dropdown, Typography } from 'shared/ui'
import { SingleRowWithPlus } from 'shared/ui/SingleRowWithPlus/SingleRowWithPlus.tsx'
import { sameWidth } from 'shared/lib'
import { MultiSelectDropdownContent } from 'entities/Phone/ui/AssignNumberCommon/ui/NewInboxContent/MultiSelectDropdownContent.tsx'
import { ChipWithAvatar } from 'entities/Phone/ui/AssignNumberCommon/ui/ChipWithAvatar/ChipWithAvatar.tsx'
import styles from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/styles.module.scss'
import { WidgetItem } from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/ui/Widgets'
import { type HandOffActionWidgetStore } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/ui/HandOffActionWidget/store/handOffActionWidgetStore.ts'
import { useWidgetKeyboard } from 'pages/chatbot/ui/UnifiedAgent/hooks/useWidgetKeyboard.ts'
import { fnKey } from 'pages/chatbot/ui/UnifiedAgent/lib/getLastStepHintMessage.ts'

export const HandoffWhoNotifiedStep = observer(({ store }: { store: HandOffActionWidgetStore }) => {
  const { handoffNotifySettingsStore, setHint } = store
  const {
    notifyConversationAssignee,
    setNotifyConversationAssignee,
    notifyContactOwner,
    setNotifyContactOwner,
    notifyAllInboxMembers,
    setNotifyAllInboxMembers,
    notify_additional_users,
    multiSelectMembersStore,
  } = handoffNotifySettingsStore

  const [focusedIndex, setFocusedIndex] = useState(0)

  const [openDropdownTrigger, setOpenDropdownTrigger] = useState('')

  const items = [
    {
      name: 'Conversations assignee',
      checked: notifyConversationAssignee,
      setChecked: setNotifyConversationAssignee,
    },
    {
      name: 'Contact owner',
      checked: notifyContactOwner,
      setChecked: setNotifyContactOwner,
    },
    {
      name: 'All inbox members',
      checked: notifyAllInboxMembers,
      setChecked: setNotifyAllInboxMembers,
    },
  ]
  const dropdownIndex = items.length

  useWidgetKeyboard({
    onNavigate: (direction) => {
      setFocusedIndex((prevState) => {
        const newIndex = prevState + direction
        if (newIndex < 0) {
          return prevState
        }
        if (newIndex > items.length) {
          return prevState
        }
        return newIndex
      })
    },
    onEnter: () => {
      if (focusedIndex === dropdownIndex) {
        setOpenDropdownTrigger(nanoid())
      } else {
        items[focusedIndex].setChecked?.(!items[focusedIndex].checked)
      }
    },
    onNext: store.onNext,
    onEscape: store.handleClose,
  })
  useEffect(() => {
    setHint(['↑↓ to navigate', 'Enter to select', `${fnKey}Enter to submit`, 'Esc to skip'])
    return () => {
      setHint([])
    }
  }, [])

  const { membersDropdownItems, viewSelectedMembers, setSelectedMember, deleteSelectedMember } =
    multiSelectMembersStore

  return (
    <div className={styles.list}>
      {items.map(({ name, checked, setChecked }, index) => (
        <WidgetItem
          key={name}
          isFocused={index === focusedIndex}
          onClick={() => {
            setFocusedIndex(index)
            setChecked(!checked)
          }}
          leftNode={<Checkbox size={20} checked={checked} ariaLabel={name} />}
        >
          <Typography variant={'body-rg-regular'} ariaLabel={name}>
            {name}
          </Typography>
        </WidgetItem>
      ))}
      <Dropdown
        openTrigger={openDropdownTrigger}
        width={'100%'}
        PopperProps={{
          modifiers: [
            sameWidth(),
            {
              name: 'offset',
              options: {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                offset: ({ reference }) => {
                  return [0, -reference.height - 4]
                },
              },
            },
          ],
        }}
        triggerComponent={() => (
          <WidgetItem
            isFocused={focusedIndex === 3}
            onClick={() => {
              setFocusedIndex(3)
            }}
            leftNode={
              <Checkbox
                size={20}
                checked={!!notify_additional_users.length}
                ariaLabel={'Additional members'}
              />
            }
          >
            {viewSelectedMembers.length ? (
              <SingleRowWithPlus
                items={viewSelectedMembers}
                renderItem={(item) => <ChipWithAvatar item={item} label={item.label} />}
              />
            ) : (
              <Typography
                variant={'body-rg-regular'}
                ariaLabel={'Additional members'}
                color={'var(--content-neutral-primary)'}
              >
                Additional members...
              </Typography>
            )}
          </WidgetItem>
        )}
        customComponent={() => (
          <MultiSelectDropdownContent
            ChipComponent={ChipWithAvatar}
            items={membersDropdownItems}
            selectedItems={viewSelectedMembers}
            onSelect={setSelectedMember}
            onDelete={deleteSelectedMember}
            shouldShowNotFoundText
          />
        )}
      />
    </div>
  )
})
